import { useCallback, useEffect, useRef, useState } from 'react'

import { Form } from 'antd'
import { DraggerProps, RcFile } from 'antd/es/upload'
import axios, { CancelTokenSource } from 'axios'
import { v4 } from 'uuid'

import { useOrgUuid } from 'src/hooks/use-org-uuid'
import { filterNil } from 'src/utils'
import { monitoring } from 'src/utils/monitoring'

import {
  CreateFileMutationVariables,
  GetFileUploadCredentialsMutationVariables,
  ThumbnailFileFragment,
} from '../../types/graphql'
import { MaxFileSize } from '../constants'
import { PathPrefixes } from '../constants/file'
import { useCreateFileMutation, useGetFileUploadCredentialsMutation } from '../fetch/files'
import { useIsCitizen } from '../layouts/AppStateContextLayout/utils'
import {
  FileUploadErrors,
  getFileName,
  getIsFileExtensionAccepted,
  isImage,
  IUploader,
  uploadFile as uploadFileBase,
  validatePdf,
} from '../utils/file'

import { FileUploadStatuses } from './use-file-upload-state'

export interface UploadMultipleFilesFileUploadState {
  path: string
  status: FileUploadStatuses
  progress: number
  id: string
  name: string
  url: string | undefined
  thumbnailUrl: string | undefined
  size: number | undefined
  type: string | undefined
  govWellFileId: number | undefined
  cancelToken: CancelTokenSource | undefined
  errorType: string | undefined
  govWellFile: ThumbnailFileFragment | undefined
}

export interface FilesUploadState {
  beforeUpload: NonNullable<DraggerProps['beforeUpload']>
  govWellFileIds: number[]
  loading: boolean
  removeFile: (id: string) => void
  reset: () => void
  setName: (id: string, fileName: string) => void
  states: UploadMultipleFilesFileUploadState[]
}

export interface UseFilesUploadStateParams {
  inputBucket?: string
  pathPrefix: PathPrefixes
  afterUpload?: (fileId: number, path: string) => Promise<void>
  fieldName?: string
  accept?: string
  shouldAnnotateImageWithExifContext?: boolean
  shouldUploadFileToOpenAI?: boolean
}

const getFileStateById = (
  fileStates: UploadMultipleFilesFileUploadState[],
  id: string
): UploadMultipleFilesFileUploadState | undefined => {
  return fileStates.find((state) => state.id === id)
}

export const mapFileFragmentToUploadMultipleFilesFileUploadState = (
  fileFragment: ThumbnailFileFragment
): UploadMultipleFilesFileUploadState => {
  return {
    path: fileFragment?.path,
    name: fileFragment?.name ?? '',
    url: fileFragment?.url?.signedUrl,
    thumbnailUrl: fileFragment?.thumbnailUrl?.signedUrl,
    status: fileFragment ? FileUploadStatuses.Existing : FileUploadStatuses.Default,
    size: fileFragment?.size ?? 0,
    type: fileFragment?.type,
    progress: 0,
    id: v4(),
    govWellFileId: fileFragment?.id,
    errorType: '',
    cancelToken: undefined,
    govWellFile: fileFragment,
  }
}

export const useFilesUploadState = (params: UseFilesUploadStateParams): FilesUploadState => {
  const {
    inputBucket,
    pathPrefix,
    afterUpload,
    fieldName,
    accept,
    shouldAnnotateImageWithExifContext,
    shouldUploadFileToOpenAI,
  } = params
  const orgUuid = useOrgUuid()
  const bucket = inputBucket || orgUuid

  const form = Form.useFormInstance()
  Form.useWatch?.(fieldName, form)
  const existingFormStates: UploadMultipleFilesFileUploadState[] | undefined = fieldName
    ? form?.getFieldValue(fieldName)
    : undefined
  const getInitialStates = useCallback((): UploadMultipleFilesFileUploadState[] => {
    if (Array.isArray(existingFormStates)) {
      return existingFormStates
    }
    return []
  }, [existingFormStates])

  const [states, setStates] = useState<UploadMultipleFilesFileUploadState[]>(getInitialStates())
  const uploaderRefsById = useRef<Map<string, IUploader>>(new Map())
  const statesRef = useRef<UploadMultipleFilesFileUploadState[]>(states)
  const handleStatesChange = useCallback(
    (
      arg:
        | UploadMultipleFilesFileUploadState[]
        | ((
            newStates: UploadMultipleFilesFileUploadState[]
          ) => UploadMultipleFilesFileUploadState[])
    ) => {
      const newStates = Array.isArray(arg) ? arg : arg(statesRef.current)
      statesRef.current = newStates
      setStates(newStates)
    },
    []
  )

  const [fileIdMap] = useState<Set<string>>(new Set<string>())

  const initializeNewFileUploads = useCallback(
    (files: RcFile[]) => {
      const newStates: UploadMultipleFilesFileUploadState[] = files.flatMap((file: RcFile) => {
        const localPreviewUrl = isImage(file.type) ? URL.createObjectURL(file) : undefined
        return [
          {
            path: '',
            name: getFileName(file),
            url: localPreviewUrl,
            thumbnailUrl: localPreviewUrl,
            status: FileUploadStatuses.Default,
            size: file?.size,
            type: file.type,
            progress: 0,
            id: v4(),
            govWellFileId: undefined,
            errorType: '',
            cancelToken: axios.CancelToken.source(),
            govWellFile: undefined,
          },
        ]
      })
      handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => [
        ...prevStates,
        ...newStates,
      ])
      return newStates
    },
    [handleStatesChange]
  )

  const setFormFieldState = useCallback(
    (fieldStates: UploadMultipleFilesFileUploadState[]) => {
      if (form && fieldName) {
        form.setFieldValue(fieldName, fieldStates)
      }
    },
    [fieldName, form]
  )

  const removeFile = (id: string) => {
    const uploader = uploaderRefsById.current.get(id)
    if (uploader) {
      uploader.abort()
    }
    handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => {
      const fileState = prevStates.find((state) => state.id === id)
      if (fileState?.thumbnailUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(fileState.thumbnailUrl)
      }
      const index = prevStates.findIndex((state) => state.id === id)
      if (index < 0) {
        return prevStates // file removed from form
      }
      const newStates = [...prevStates.slice(0, index), ...prevStates.slice(index + 1)]
      setFormFieldState(newStates)
      return newStates
    })
  }

  const getProperty = useCallback(
    <TKey extends keyof UploadMultipleFilesFileUploadState>(
      fileStates: UploadMultipleFilesFileUploadState[],
      id: string,
      property: TKey
    ): UploadMultipleFilesFileUploadState[TKey] | undefined => {
      const fileState = getFileStateById(fileStates, id)
      return fileState?.[property]
    },
    []
  )

  const updateProperty = useCallback(
    <TKey extends keyof UploadMultipleFilesFileUploadState>(
      fileStates: UploadMultipleFilesFileUploadState[],
      id: string,
      property: TKey,
      value: UploadMultipleFilesFileUploadState[TKey]
    ) => {
      const fileState = getFileStateById(fileStates, id)
      if (fileState) {
        fileState[property] = value
      }
    },
    []
  )

  const setPath = useCallback(
    (id: string, path: string) => {
      handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => {
        const newStates = [...prevStates]
        updateProperty(newStates, id, 'path', path)
        setFormFieldState(newStates)
        return newStates
      })
    },
    [handleStatesChange, setFormFieldState, updateProperty]
  )
  const setFileUploadStatus = useCallback(
    (id: string, status: FileUploadStatuses) => {
      handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => {
        const newStates = [...prevStates]
        updateProperty(newStates, id, 'status', status)
        setFormFieldState(newStates)
        return newStates
      })
    },
    [handleStatesChange, setFormFieldState, updateProperty]
  )
  const setFileUploadProgress = useCallback(
    (id: string, progress: number) => {
      handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => {
        const newStates = [...prevStates]
        updateProperty(newStates, id, 'progress', progress)
        setFormFieldState(newStates)
        return newStates
      })
    },
    [handleStatesChange, setFormFieldState, updateProperty]
  )
  const setFileName = useCallback(
    (id: string, name: string) => {
      handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => {
        const newStates = [...prevStates]
        updateProperty(newStates, id, 'name', name)

        const govWellFile: ThumbnailFileFragment | undefined = getProperty(
          newStates,
          id,
          'govWellFile'
        )
        if (govWellFile) {
          updateProperty(newStates, id, 'govWellFile', {
            ...govWellFile,
            name,
          })
        }

        setFormFieldState(newStates)
        return newStates
      })
    },
    [getProperty, handleStatesChange, setFormFieldState, updateProperty]
  )
  const setErrorType = useCallback(
    (id: string, errorType: string) => {
      handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => {
        const newStates = [...prevStates]
        updateProperty(newStates, id, 'errorType', errorType)
        setFormFieldState(newStates)
        return newStates
      })
    },
    [handleStatesChange, setFormFieldState, updateProperty]
  )
  const setFileSize = useCallback(
    (id: string, size: number | undefined) => {
      handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => {
        const newStates = [...prevStates]
        updateProperty(newStates, id, 'size', size)
        setFormFieldState(newStates)
        return newStates
      })
    },
    [handleStatesChange, setFormFieldState, updateProperty]
  )
  const setGovWellFile = useCallback(
    (id: string, fileFragment: ThumbnailFileFragment) => {
      handleStatesChange((prevStates: UploadMultipleFilesFileUploadState[]) => {
        const newStates = [...prevStates]
        const fileState = getFileStateById(newStates, id)
        if (fileState) {
          fileState.govWellFileId = fileFragment.id
          fileState.govWellFile = fileFragment
          fileState.thumbnailUrl = fileState.thumbnailUrl?.startsWith('blob:')
            ? fileState.thumbnailUrl
            : (fileFragment.thumbnailUrl?.signedUrl ?? fileState.thumbnailUrl)
          fileState.type = fileFragment.type
          fileState.url = fileFragment.url?.signedUrl
        }
        setFormFieldState(newStates)
        return newStates
      })
    },
    [handleStatesChange, setFormFieldState]
  )

  const { mutateAsync: createFileRaw } = useCreateFileMutation()
  const createFile = useCallback(
    async (input: CreateFileMutationVariables) => {
      const res = await createFileRaw(input)
      return res.createFile
    },
    [createFileRaw]
  )

  const { mutateAsync: getFileUploadCredentialsRaw } = useGetFileUploadCredentialsMutation()
  const getFileUploadCredentials = useCallback(
    async (input: GetFileUploadCredentialsMutationVariables) => {
      const res = await getFileUploadCredentialsRaw(input)
      return res.getFileUploadCredentials
    },
    [getFileUploadCredentialsRaw]
  )

  const isCitizen = useIsCitizen()

  const uploadFile = useCallback(
    async (id: string, file: RcFile, newState: UploadMultipleFilesFileUploadState) => {
      try {
        if (isCitizen && file.size > MaxFileSize) {
          setErrorType(id, FileUploadErrors.TooBig)
          setFileUploadStatus(id, FileUploadStatuses.Error)
          return
        }

        const pdfError = await validatePdf(file)
        if (pdfError) {
          setErrorType(id, pdfError)
          setFileUploadStatus(id, FileUploadStatuses.Error)
          return
        }

        if (!getIsFileExtensionAccepted({ accept, fileName: file.name })) {
          setErrorType(id, FileUploadErrors.UnsupportedFileType)
          setFileUploadStatus(id, FileUploadStatuses.Error)
          return
        }

        if (!bucket || !newState.cancelToken) {
          return
        }

        const newFile = await uploadFileBase({
          bucket,
          createFile,
          file,
          getFileUploadCredentials,
          onError: (errorType: string) => setErrorType(newState.id, errorType),
          pathPrefix,
          setFileName: (name: string) => setFileName(id, name),
          setFileSize: (size: number) => setFileSize(id, size),
          setFileUploadProgress: (progress: number) => setFileUploadProgress(id, progress),
          setFileUploadStatus: (status: FileUploadStatuses) => setFileUploadStatus(id, status),
          setGovWellFile: (govWellFile: ThumbnailFileFragment) => setGovWellFile(id, govWellFile),
          setPath: (path: string) => setPath(id, path),
          setUploaderRef: (uploader: IUploader) => {
            uploaderRefsById.current.set(id, uploader)
          },
          shouldAnnotateImageWithExifContext,
          shouldUploadFileToOpenAI,
        })
        if (newFile) {
          const { id: fileId, path: newPath } = newFile
          await afterUpload?.(fileId, newPath)
        }
      } catch (e) {
        monitoring.logError(e, { context: 'uploadFile:multi' })
        setErrorType(id, FileUploadErrors.UploadFailed)
        setFileUploadStatus(id, FileUploadStatuses.Error)
      }
    },
    [
      accept,
      afterUpload,
      bucket,
      createFile,
      getFileUploadCredentials,
      isCitizen,
      pathPrefix,
      setErrorType,
      setFileName,
      setFileSize,
      setFileUploadProgress,
      setFileUploadStatus,
      setGovWellFile,
      setPath,
      shouldAnnotateImageWithExifContext,
      shouldUploadFileToOpenAI,
    ]
  )

  const reset = useCallback(() => handleStatesChange([]), [handleStatesChange])

  const beforeUpload = useCallback(
    async (_: RcFile, fileList: RcFile[]) => {
      const fileId = fileList[0]?.uid
      if (fileId && fileIdMap.has(fileId)) {
        return
      }
      const newIds = fileList.map((file: RcFile) => file.uid)
      newIds.forEach((id) => fileIdMap.add(id))

      const filteredFileList = fileList.filter((file: RcFile) => !!file)
      const newStates = initializeNewFileUploads(filteredFileList)
      await Promise.all(
        fileList.map((file: RcFile, index: number) => {
          const fileState = newStates[index]
          if (!fileState) {
            return Promise.resolve()
          }
          return uploadFile(fileState.id, file, fileState)
        })
      )
      return false
    },
    [fileIdMap, initializeNewFileUploads, uploadFile]
  )

  const loading = states.some(
    (state: UploadMultipleFilesFileUploadState) => state.status === FileUploadStatuses.Uploading
  )
  const govWellFileIds = filterNil(
    states
      .filter((s) => !s.errorType)
      .map((state: UploadMultipleFilesFileUploadState) => state.govWellFileId)
      .filter((id) => !!id)
  )

  useEffect(() => {
    // Sync form state with regular state in case the files field value is modified via form state, e.g. via form auto-fill
    if (
      existingFormStates?.length &&
      existingFormStates.some((fileFragment, index) => states[index]?.path !== fileFragment.path)
    ) {
      reset()
      handleStatesChange(getInitialStates())
    }
  }, [
    existingFormStates,
    getInitialStates,
    handleStatesChange,
    reset,
    setFileName,
    setFileSize,
    setFileUploadProgress,
    setFileUploadStatus,
    setPath,
    states,
  ])

  return {
    states,
    loading,
    govWellFileIds,
    beforeUpload,
    reset,
    removeFile,
    setName: setFileName,
  }
}
